<template>
	<div
		:class="{
			'upload-button--editable': editable
		}"
		:tabindex="tabIndex"
		:aria-label="ariaLabel"
		class="upload-button"
		data-cy="upload-button"
		role="button"
	>
		<label
			v-if="label"
			class="upload-button__label"
		>
			{{ label }}
		</label>

		<div
			v-if="!media || !media.thumbnail"
			:class="`upload-button__button--${size}`"
			class="upload-button__button"
			data-cy="upload-button__button"
			@click="handleClick"
		>
			<helio-icon
				:token="type === 'image' ? 'portrait' : 'video'"
				weight="light"
				class="upload-button__icon"
			/>

			<label class="upload-button__text">
				{{ dynamicText }}
			</label>
		</div>

		<div
			v-if="media && media.thumbnail"
			class="upload-button__thumbnail"
		>
			<img :src="media.thumbnail">

			<div
				class="upload-button__options"
				@click="handleClick"
			>
				<helio-icon
					:class="{
						'upload-button__option--view' : (type === 'image'),
						'upload-button__option--play' : (type === 'video'),
					}"
					:token="type === 'video' ? 'play' : 'eye'"
					:weight="type === 'video' ? 'light' : 'regular'"
					class="upload-button__option upload-button__option--left"
					:tabindex="tabIndex"
					role="button"
					:aria-label="`preview ${type}`"
					@click.stop="changePreview(true)"
				/>

				<helio-icon
					v-if="editable"
					class="upload-button__option upload-button__option--left"
					token="pen"
					weight="light"
					:tabindex="tabIndex"
					role="button"
					:aria-label="`edit ${type}`"
					@click="handleClick"
				/>

				<helio-icon
					v-if="editable"
					class="upload-button__option upload-button__option--right"
					token="times"
					weight="regular"
					:tabindex="tabIndex"
					role="button"
					:aria-label="`remove ${type}`"
					@click.stop="$emit('remove')"
				/>
			</div>
		</div>

		<portal :to="portal">
			<helio-overlay
				v-if="media && preview"
				:title="previewTitle"
				:subtitle="media.name"
				class="upload-button__preview"
				@close="changePreview(false)"
			>
				<template v-slot:content>
					<img
						v-if="type === 'image' && media && media.url"
						:src="media.url"
						class="upload-button__image-preview"
					>

					<vue-plyr
						v-if="type === 'video' && media && media.url"
						class="upload-button__video-preview"
					>
						<div class="plyr__video-embed">
							<iframe
								:src="videoUrl"
								allow="autoplay"
							/>
						</div>
					</vue-plyr>
				</template>

				<template v-slot:footer>
					<helio-button-group
						alignment="right"
						:segmented="false"
					>
						<helio-button
							:text="$t('upload-button.preview.close')"
							@click="changePreview(false)"
						/>
					</helio-button-group>
				</template>
			</helio-overlay>
		</portal>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';
import Overlay from '../overlay/Overlay.vue';
import Button from '../button/Button.vue';
import ButtonGroup from '../button-group/ButtonGroup.vue';

export default {
	name: 'UploadButton',

	components: {
		'helio-icon': Icon,
		'helio-overlay': Overlay,
		'helio-button': Button,
		'helio-button-group': ButtonGroup,
	},

	props: {
		editable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the media associated with the UploadButton can be edited',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the label of the UploadButton',
		},

		media: {
			type: Object,
			required: false,
			default: null,
			description: 'Supplies data for each media file to be uploaded',
		},

		multiple: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether more than one media file can be uploaded with the UploadButton',
		},

		portal: {
			type: String,
			required: false,
			default: 'overlays',
			description: 'Sets the target portal to use',
		},

		size: {
			type: String,
			required: false,
			default: 'medium',
			validator: value => [
				'large',
				'medium',
				'small',
			].indexOf(value) !== -1,
			description: 'Sets the size of the UploadButton',
		},

		text: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text in the UploadButton',
		},

		type: {
			type: String,
			required: false,
			default: 'image',
			validator: value => [
				'image',
				'video',
			].indexOf(value) !== -1,
			description: 'Sets the type of media that can be uploaded via the UploadButton',
		},
	},

	data() {
		return {
			preview: false,
			tabIndex: 0,
		};
	},

	computed: {
		dynamicText() {
			if (this.text) {
				return this.text;
			}

			const multipleLabel = this.multiple ? 'multiple' : 'single';

			return this.$t(`upload-button.${this.type}-${multipleLabel}`);
		},

		ariaLabel() {
			if (this.label) {
				return this.label;
			}

			if (!this.label && this.dynamicText) {
				return this.dynamicText;
			}

			return `edit ${this.type}`;
		},

		videoUrl() {
			if (this.media.url.includes('youtube.com')) {
				const params = '?iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0&enablejsapi=1';
				return `${this.media.url}${params}`;
			}

			if (this.media.url.includes('vimeo.com')) {
				const params = '?loop=false&amp;byline=false&amp;portrait=false&amp;title=false&amp;speed=true&amp;transparent=0&amp;gesture=media';
				const id = this.media.url.replace('https://vimeo.com/', '');
				return `https://player.vimeo.com/video/${id}${params}`;
			}

			return null;
		},

		previewTitle() {
			if (this.media.type === 'image') {
				return this.$t('upload-button.preview.image-title');
			}

			return this.$t('upload-button.preview.video-title');
		},
	},

	watch: {
		preview() {
			// We are making sure the overlay is rendering well
			setTimeout(() => {
				window.dispatchEvent(new Event('resize'));
			}, 1000);
		},
	},

	methods: {
		handleClick(event) {
			if (this.editable) {
				this.$emit('click', event);
			}
		},

		changePreview(status) {
			this.preview = status;
			this.$emit('preview-changed', status);
		},
	},
};
</script>

<style lang="scss" src="./upload-button.scss" />
